import { useLayoutEffect, useRef } from 'react';
import { useLocation, useSearch } from 'wouter';
import { toast } from '@/hooks/use-toast';
import { useCanvasStore } from './canvas-store';
import { parseWorkspaceRoute, workspaceUrl } from './workspace-routes';

const browserUrl = () => window.location.pathname + window.location.search;

/** The URL chooses the visible section; existing task buckets remain in the
 * shared store. Store-driven shortcuts also publish a navigable URL. */
export function useWorkspaceRoutes() {
  const [path, navigate] = useLocation();
  const search = useSearch();
  const appliedUrl = useRef('');
  const applying = useRef(false);

  useLayoutEffect(() => {
    appliedUrl.current = browserUrl();
    applying.current = true;
    const route = parseWorkspaceRoute(path, search);
    const state = useCanvasStore.getState();
    if (route.taskId != null) {
      const requestedUrl = appliedUrl.current;
      const taskId = route.taskId;
      const cachedTask = state.projects[taskId];
      // Repair the target bucket before switchProject can yield. Hydration
      // writes also notify the reverse URL subscriber; leaving a stale browse
      // view here would navigate away before the load finishes.
      if (cachedTask && cachedTask.activeView !== 'chat-only' && cachedTask.activeView !== 'chat-canvas') {
        state._writeProjectView(taskId, { activeView: cachedTask.isCanvasOpen || cachedTask.images.length ? 'chat-canvas' : 'chat-only' });
      }
      const showTask = () => {
        if (browserUrl() !== requestedUrl || useCanvasStore.getState().activeProjectId !== taskId) return;
        const task = useCanvasStore.getState().projects[taskId];
        useCanvasStore.setState({ folderProjectId: task?.parentId ?? null });
        // Older cached tasks can contain a browse section in their view slot.
        // A task URL must repair that even when no project fetch is needed.
        if (task && task.activeView !== 'chat-only' && task.activeView !== 'chat-canvas') {
          useCanvasStore.getState().setActiveView(task.isCanvasOpen || task.images.length ? 'chat-canvas' : 'chat-only');
        }
      };
      if (state.activeProjectId !== taskId || !state.projects[taskId]?.loadedAt) {
        void state.switchProject(taskId).then(showTask).catch(error => {
          if (browserUrl() === requestedUrl) toast({ title: 'Could not open task', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' });
        });
      } else {
        showTask();
      }
    } else {
      useCanvasStore.setState({ activeProjectId: null, activeTaskId: null, standaloneActiveView: route.view,
        ...(route.view === 'chat-only' ? { folderProjectId: route.folderId, ...((route.folderId == null || state.activeProjectId != null || state.folderProjectId !== route.folderId) ? { activeBrandId: null, activeAdAccountId: null } : {}) } : {}) });
      if (route.folderId != null) {
        const requestedUrl = appliedUrl.current;
        void fetch(`/api/canvas/projects/${route.folderId}`).then(async response => {
          if (!response.ok) throw new Error('Could not load the task folder.');
          const folder = await response.json();
          if (browserUrl() === requestedUrl && folder.id === route.folderId) {
            useCanvasStore.setState({ activeBrandId: folder.brandId ?? folder.brand_id ?? null, activeAdAccountId: folder.adAccountId ?? folder.ad_account_id ?? null });
          }
        }).catch(error => {
          if (browserUrl() === requestedUrl) toast({ title: 'Could not open folder', description: error.message, variant: 'destructive' });
        });
      }
    }
    applying.current = false;
  }, [path, search]);

  useLayoutEffect(() => {
    let queued = false;
    let alive = true;
    const unsubscribe = useCanvasStore.subscribe(() => {
      if (applying.current || queued) return;
      queued = true;
      // Sidebar actions update several store fields synchronously. Publish
      // only their final destination, never an intermediate New Task entry.
      queueMicrotask(() => {
        queued = false;
        if (!alive || applying.current || appliedUrl.current !== browserUrl()) return;
        const state = useCanvasStore.getState();
        const view = state.activeProjectId == null ? state.standaloneActiveView : state.projects[state.activeProjectId]?.activeView ?? 'chat-only';
        const destination = workspaceUrl(view, state.activeProjectId, state.folderProjectId);
        const current = parseWorkspaceRoute(window.location.pathname, window.location.search);
        if (destination !== workspaceUrl(current.view, current.taskId, current.folderId)) navigate(destination);
      });
    });
    return () => { alive = false; unsubscribe(); };
  }, [navigate]);
}

export function prepareNewTaskForTour() {
  // Detach the pointer; never clear the previous task's images or messages.
  useCanvasStore.setState({ activeProjectId: null, activeTaskId: null, folderProjectId: null,
    activeBrandId: null, activeAdAccountId: null, standaloneActiveView: 'chat-only', tourRequest: 'forced' });
}
